'use client';

import { useEffect, useRef, useState } from 'react';
import { FiArrowLeft } from 'react-icons/fi';

import { BrandIcon } from '@/components/brand-icon';

const LENGTH = 6;

/** Insere `char` no índice e devolve a string compactada (sem espaços). */
function setCharAtPure(value: string, index: number, char: string): string {
  const chars = (value + ' '.repeat(LENGTH)).slice(0, LENGTH).split('');
  chars[index] = char || ' ';
  return chars.join('').replace(/ /g, '').slice(0, LENGTH);
}

interface AdminOtpCardProps {
  email: string;
  value: string;
  onChange: (next: string) => void;
  onSubmit: (code: string) => void;
  onResend: () => void;
  onBack: () => void;
  resendIn: number;
  loading?: boolean;
  error?: string | null;
}

/**
 * Card de OTP do admin (6 boxes), mesmo comportamento do card do app
 * (auto-avança, colar, auto-submit ao completar), mas com estilo escopado em
 * `.admin-auth` e renderizado sobre o palco cinematográfico do admin.
 */
export function AdminOtpCard({
  email,
  value,
  onChange,
  onSubmit,
  onResend,
  onBack,
  resendIn,
  loading,
  error,
}: AdminOtpCardProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [innerValue, setInnerValue] = useState(value);
  const submittedRef = useRef('');

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (innerValue.length === LENGTH && submittedRef.current !== innerValue && !loading) {
      submittedRef.current = innerValue;
      onSubmit(innerValue);
    }
    if (innerValue.length === 0) {
      submittedRef.current = '';
    }
  }, [innerValue, loading, onSubmit]);

  function update(updater: (prev: string) => string) {
    setInnerValue((prev) => {
      const next = updater(prev);
      onChange(next);
      return next;
    });
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const raw = e.target.value.replace(/\D/g, '');
    if (!raw) {
      update((prev) => setCharAtPure(prev, index, ''));
      return;
    }
    if (raw.length > 1) {
      let nextLen = 0;
      update((prev) => {
        const next = (prev.slice(0, index) + raw).replace(/\D/g, '').slice(0, LENGTH);
        nextLen = next.length;
        return next;
      });
      requestAnimationFrame(() => {
        refs.current[Math.min(nextLen, LENGTH - 1)]?.focus();
      });
      return;
    }
    update((prev) => setCharAtPure(prev, index, raw));
    if (index < LENGTH - 1) refs.current[index + 1]?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>, index: number) {
    if (e.key === 'Backspace' && !innerValue[index] && index > 0) {
      e.preventDefault();
      update((prev) => setCharAtPure(prev, index - 1, ''));
      refs.current[index - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      refs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < LENGTH - 1) {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LENGTH);
    if (!pasted) return;
    e.preventDefault();
    update(() => pasted);
    refs.current[Math.min(pasted.length, LENGTH - 1)]?.focus();
  }

  return (
    <div className="card">
      <div className="inner">
        <BrandIcon size={56} className="logo stg d1" alt="Trakd" />
        <div className="badge stg d2">
          <span className="dot" />
          acesso admin
        </div>
        <h1 className="title stg d2">Confirme seu acesso</h1>
        <p className="sub stg d3">
          Enviamos um código de 6 dígitos para
          <br />
          <b>{email}</b>
        </p>

        <div className="otpwrap stg d4" role="group" aria-label="Código de 6 dígitos">
          {Array.from({ length: LENGTH }).map((_, i) => (
            <input
              key={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              className="otp"
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={LENGTH}
              value={innerValue[i] ?? ''}
              onChange={(e) => handleChange(e, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={handlePaste}
              disabled={loading}
              aria-label={`Dígito ${i + 1}`}
            />
          ))}
        </div>

        {error && <p className="err stg d4">{error}</p>}

        <p className="resend stg d4">
          Não recebeu o código?{' '}
          <button type="button" onClick={onResend} disabled={resendIn > 0 || loading}>
            {resendIn > 0 ? `Reenviar em ${resendIn}s` : 'Reenviar'}
          </button>
        </p>

        <button type="button" className="back stg d5" onClick={onBack}>
          <FiArrowLeft size={14} /> Voltar e trocar o e-mail
        </button>
      </div>
    </div>
  );
}
